import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Compass } from "lucide-react";
import { useWalkthrough } from "src/contexts/WalkthroughContext";

const WalkthroughWelcome: FC = () => {
  const { t } = useTranslation("walkthrough");
  const { isActive, currentStep, nextStep, skipTour } = useWalkthrough();

  const isVisible = isActive && currentStep === 0;

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <motion.div
            className="relative w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Header accent */}
            <div className="h-1.5 bg-gradient-to-r from-blue-500 via-primary-500 to-primary-500" />

            <div className="p-6 sm:p-8 text-center">
              {/* Icon */}
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
                <Compass className="h-7 w-7 text-blue-600" />
              </div>

              {/* Logo */}
              <div className="flex items-center justify-center gap-2 mb-3">
                <img
                  src="/images/botbat-logo.png"
                  alt="Botbat"
                  className="h-6 w-6"
                />
                <span className="text-sm font-medium text-gray-500">
                  ~5 min
                </span>
              </div>

              {/* Title */}
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {t("steps.welcome.title")}
              </h2>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                {t("steps.welcome.description")}
              </p>

              {/* Actions */}
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                <button
                  onClick={nextStep}
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {t("navigation.next")}
                </button>
                <button
                  onClick={skipTour}
                  className="inline-flex items-center justify-center rounded-lg px-6 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  {t("navigation.skip")}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default WalkthroughWelcome;
